import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Pagination,
  Slider,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Favorite,
  FavoriteBorder,
  LocationOn,
  CheckCircleOutline,
  Bolt,
  WaterDrop,
  Waves,
  LocalFireDepartment,
  Phone,
  Wifi,
  Tv,
  ExpandMore,
  Clear,
  DirectionsCar,
  Fence,
  Description,
  Map
} from '@mui/icons-material';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const Properties = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [favorites, setFavorites] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [propertiesPerPage] = useState(9);
  const [successMessage, setSuccessMessage] = useState('');

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState([0, 200000000]); // 0 to 200 million KSH
  const [landType, setLandType] = useState('');
  const [sizeRange, setSizeRange] = useState([0, 1000]); // 0 to 1000 acres
  const [location, setLocation] = useState('');
  const [utilities, setUtilities] = useState([]);

  // Fetch properties from the database
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        
        const { data: properties, error } = await supabase
          .from('properties')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        setProperties(properties);
        setFilteredProperties(properties);
        setError('');
      } catch (err) {
        console.error('Error fetching properties:', err);
        setError('Failed to load properties. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProperties();
  }, []);

  // Handle success message from navigation state
  useEffect(() => {
    if (routerLocation.state?.message) {
      setSuccessMessage(routerLocation.state.message);
      // Clear the success message after 5 seconds
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      // Clear the navigation state to prevent showing message on refresh
      navigate(routerLocation.pathname, { replace: true, state: {} });
      return () => clearTimeout(timer);
    }
  }, [routerLocation.state, navigate, routerLocation.pathname]);

  useEffect(() => {
    if (properties.length > 0) {
      console.log('First property data:', properties[0]);
      console.log('City:', properties[0].city);
      console.log('State:', properties[0].state);
      console.log('Location:', properties[0].location);
    }
  }, [properties]);

  const toggleFavorite = (propertyId) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(propertyId)) {
        newFavorites.delete(propertyId);
      } else {
        newFavorites.add(propertyId);
      }
      return newFavorites;
    });
  };

  const formatPrice = (price, area) => {
    const formattedPrice = new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
    
    return formattedPrice;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setPriceRange([0, 200000000]); // 0 to 200 million KSH
    setLandType('');
    setSizeRange([0, 1000]); // 0 to 1000 acres
    setLocation('');
    setUtilities([]);
  };

  const handleUtilityToggle = (utility) => {
    setUtilities(prev => 
      prev.includes(utility)
        ? prev.filter(u => u !== utility)
        : [...prev, utility]
    );
  };

  // Filter properties based on search criteria
  useEffect(() => {
    let filtered = properties.filter(property => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        property.title.toLowerCase().includes(searchLower) ||
        property.description.toLowerCase().includes(searchLower) ||
        property.address.toLowerCase().includes(searchLower) ||
        property.city.toLowerCase().includes(searchLower) ||
        property.state.toLowerCase().includes(searchLower);
      
      const matchesLandType = !landType || property.property_type === landType;
      const matchesPrice = property.price >= priceRange[0] && property.price <= priceRange[1];
      const matchesSize = property.area >= sizeRange[0] && property.area <= sizeRange[1];
      
      const locationLower = location.toLowerCase();
      const matchesLocation = 
        !location ||
        property.city.toLowerCase().includes(locationLower) ||
        property.state.toLowerCase().includes(locationLower) ||
        property.address.toLowerCase().includes(locationLower);
      
      const matchesUtilities = utilities.length === 0 || 
        (property.amenities && utilities.every(util => 
          property.amenities.includes(util)
        ));

      return matchesSearch && matchesLandType && matchesPrice &&
             matchesSize && matchesLocation && matchesUtilities;
    });

    setFilteredProperties(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [properties, searchTerm, landType, priceRange, sizeRange, location, utilities]);

  // Pagination
  const indexOfLastProperty = currentPage * propertiesPerPage;
  const indexOfFirstProperty = indexOfLastProperty - propertiesPerPage;
  const currentProperties = filteredProperties.slice(indexOfFirstProperty, indexOfLastProperty);
  const totalPages = Math.ceil(filteredProperties.length / propertiesPerPage);

  const utilityOptions = [
    'Water',
    'Electricity',
    'Sewer',
    'Road Access',
    'Fenced',
    'Title Deed',
    'Surveyed',
    'Near Tarmac'
  ];

  const landTypes = [
    { value: '', label: 'All Land Types' },
    { value: 'residential', label: 'Residential Land' },
    { value: 'commercial', label: 'Commercial Land' },
    { value: 'agricultural', label: 'Agricultural Land' },
    { value: 'industrial', label: 'Industrial Land' },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {successMessage && (
        <Box 
          sx={{ 
            mb: 3, 
            p: 2, 
            bgcolor: 'success.light', 
            color: 'success.contrastText',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <CheckCircleOutline />
          <Typography variant="body1">{successMessage}</Typography>
          <IconButton 
            size="small" 
            onClick={() => setSuccessMessage('')}
            sx={{ ml: 'auto', color: 'inherit' }}
          >
            <Clear />
          </IconButton>
        </Box>
      )}
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        Land Listings ({filteredProperties.length} found)
      </Typography>

      {/* Mobile Search Bar Only */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 3 }}>
        <TextField
          fullWidth
          label="Search land..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by location, type, or description..."
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'white'
            }
          }}
        />
      </Box>

      <Grid container spacing={4}>
        {/* Filters Sidebar - Hidden on mobile */}
        <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <FilterListIcon sx={{ mr: 1 }} />
                Filters
              </Typography>
              <Button
                size="small"
                onClick={clearFilters}
                startIcon={<Clear />}
                sx={{ textTransform: 'none' }}
              >
                Clear
              </Button>
            </Box>

            {/* Search */}
            <TextField
              fullWidth
              label="Search land..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
              }}
            />

            {/* Price Range */}
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Price Range
            </Typography>
            <Slider
              value={priceRange}
              onChange={(e, newValue) => setPriceRange(newValue)}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `$${(value / 1000).toFixed(0)}k`}
              min={0}
              max={200000000}
              step={100000}
              sx={{ mb: 3 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="body2">{formatPrice(priceRange[0])}</Typography>
              <Typography variant="body2">{formatPrice(priceRange[1])}</Typography>
            </Box>

            {/* Land Type */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Land Type</InputLabel>
              <Select
                value={landType}
                onChange={(e) => setLandType(e.target.value)}
                label="Land Type"
              >
                {landTypes.map((landType) => (
                  <MenuItem key={landType.value} value={landType.value}>
                    {landType.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Size Range */}
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Size Range
            </Typography>
            <Slider
              value={sizeRange}
              onChange={(e, newValue) => setSizeRange(newValue)}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value} acres`}
              min={0}
              max={1000}
              step={5}
              sx={{ mb: 3 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="body2">{sizeRange[0]} acres</Typography>
              <Typography variant="body2">{sizeRange[1]} acres</Typography>
            </Box>

            {/* Location */}
            <TextField
              fullWidth
              label="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              sx={{ mb: 3 }}
            />

            {/* Utilities */}
            <Accordion sx={{ boxShadow: 'none', border: 'none', '&:before': { display: 'none' } }}>
              <AccordionSummary 
                expandIcon={<ExpandMore />}
                sx={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Utilities
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormGroup>
                  {utilityOptions.map((utility) => (
                    <FormControlLabel
                      key={utility}
                      control={
                        <Checkbox
                          checked={utilities.includes(utility)}
                          onChange={() => handleUtilityToggle(utility)}
                        />
                      }
                      label={utility}
                    />
                  ))}
                </FormGroup>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>

        {/* Properties Grid */}
        <Grid item xs={12} md={9}>
          {loading ? (
            <Typography>Loading land...</Typography>
          ) : (
            <>
              <Grid container spacing={3}>
                {currentProperties.map((property) => (
                  <Grid item xs={12} sm={6} lg={4} key={property.id}>
                    <Card
                      component={Link}
                      to={`/properties/${property.id}`}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        textDecoration: 'none !important',
                        color: 'inherit',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 3,
                          textDecoration: 'none !important',
                          '& .property-actions': {
                            opacity: 1,
                          },
                        },
                        transition: 'all 0.3s ease-in-out',
                        boxShadow: 1,
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <Box 
                        className="property-actions"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          display: 'flex',
                          gap: 1,
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          borderRadius: 1,
                          p: 0.5,
                        }}
                      >
                        {/* Delete button removed */}
                      </Box>
                      
                      {/* Image with gradient overlay */}
                      <Box sx={{ 
                        position: 'relative',
                        height: 220,
                        overflow: 'hidden'
                      }}>
                        <CardMedia
                          component="img"
                          height="200"
                          image={property.images[0]}
                          alt={property.title || 'Land'}
                          onError={(e) => {
                            console.error('Error loading image:', property.images?.[0]);
                            e.target.src = '/placeholder-property.jpg';
                          }}
                          sx={{
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease-in-out',
                            '&:hover': {
                              transform: 'scale(1.02)'
                            }
                          }}
                        />
                        <Box sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '100%',
                          background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.4) 100%)',
                        }} />
                        
                        {/* Favorite button */}
                        <IconButton
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleFavorite(property.id);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(4px)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 1)',
                              transform: 'scale(1.1)'
                            },
                          }}
                        >
                          {favorites.has(property.id) ? (
                            <Favorite sx={{ color: '#f44336', fontSize: 22 }} />
                          ) : (
                            <FavoriteBorder sx={{ fontSize: 22, color: 'rgba(0,0,0,0.7)' }} />
                          )}
                        </IconButton>
                        
                        {/* Price tag */}
                        <Box sx={{
                          position: 'absolute',
                          bottom: 16,
                          left: 16,
                          backgroundColor: 'rgba(255,255,255,0.95)',
                          px: 1.5,
                          py: 0.8,
                          borderRadius: 1,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}>
                          <Typography variant="h6" sx={{ 
                            fontWeight: 700,
                            color: 'primary.main',
                            lineHeight: 1.2
                          }}>
                            {formatPrice(property.price)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Card content */}
                      <CardContent sx={{ 
                        flexGrow: 1, 
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                      }}>
                        {/* Title and location */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="h6" component="h3" sx={{ 
                            fontWeight: 700, 
                            mb: 0.5,
                            color: 'text.primary',
                            lineHeight: 1.3,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: '3.2em'
                          }}>
                            {property.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                            <LocationOn sx={{ fontSize: 18, mr: 0.8, color: 'primary.main' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {property.city?.replace(/^\s*,\s*|\s*,\s*$/g, '')}
                              {property.city && property.state ? ', ' : ''}
                              {property.state?.replace(/^\s*,\s*|\s*,\s*$/g, '')}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          {/* Property Type & Status */}
                          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                            <Chip 
                              label={property.propertyType ? property.propertyType.replace(/-/g, ' ') : 'Land'}
                              size="small"
                              sx={{
                                textTransform: 'capitalize',
                                backgroundColor: 'primary.light',
                                color: 'primary.contrastText',
                                fontWeight: 500,
                                fontSize: '0.7rem',
                              }}
                            />
                            <Chip 
                              label={property.status ? property.status.replace(/-/g, ' ') : 'For Sale'}
                              size="small"
                              color={property.status === 'sold' ? 'error' : 'success'}
                              sx={{
                                textTransform: 'capitalize',
                                fontWeight: 500,
                                fontSize: '0.7rem',
                              }}
                            />
                          </Box>

                          {/* Property Details Grid */}
                          <Grid container spacing={1} sx={{ mb: 2 }}>
                          </Grid>

                          {/* Amenities */}
                          {(property.utilities?.length > 0 || property.amenities?.length > 0) && (
                            <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ 
                                fontWeight: 600, 
                                display: 'block', 
                                mb: 1.5, 
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                fontSize: '0.7rem',
                                color: 'text.secondary',
                                opacity: 0.8
                              }}>
                                Features & Amenities
                              </Typography>
                              <Grid container spacing={1}>
                                {[...(property.utilities || []), ...(property.amenities || [])].map((util, index) => {
                                  if (!util) return null;
                                  
                                  let icon = null;
                                  let label = util;
                                  const utilLower = util.toLowerCase();
                                  
                                  // Match utility with appropriate icon
                                  if (utilLower.includes('electric')) {
                                    icon = <Bolt fontSize="small" />;
                                  } else if (utilLower.includes('water')) {
                                    icon = <WaterDrop fontSize="small" />;
                                  } else if (utilLower.includes('sewer')) {
                                    icon = <Waves fontSize="small" />;
                                  } else if (utilLower.includes('gas') || utilLower.includes('fire')) {
                                    icon = <LocalFireDepartment fontSize="small" />;
                                  } else if (utilLower.includes('phone')) {
                                    icon = <Phone fontSize="small" />;
                                  } else if (utilLower.includes('internet') || utilLower.includes('wifi')) {
                                    icon = <Wifi fontSize="small" />;
                                  } else if (utilLower.includes('tv') || utilLower.includes('television')) {
                                    icon = <Tv fontSize="small" />;
                                  } else if (utilLower.includes('road') || utilLower.includes('access')) {
                                    icon = <DirectionsCar fontSize="small" />;
                                  } else if (utilLower.includes('fence')) {
                                    icon = <Fence fontSize="small" />;
                                  } else if (utilLower.includes('deed') || utilLower.includes('title')) {
                                    icon = <Description fontSize="small" />;
                                  } else if (utilLower.includes('survey')) {
                                    icon = <Map fontSize="small" />;
                                  } else if (utilLower.includes('tarmac') || utilLower.includes('paved')) {
                                    icon = <DirectionsCar fontSize="small" />;
                                  } else {
                                    icon = <CheckCircleOutline fontSize="small" />;
                                  }
                                  
                                  return (
                                    <Grid item xs={6} key={index}>
                                      <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        mb: 1.5,
                                        '&:hover': {
                                          '& .amenity-icon': {
                                            transform: 'scale(1.1)',
                                            backgroundColor: 'rgba(25, 118, 210, 0.15)'
                                          }
                                        }
                                      }}>
                                        <Box className="amenity-icon" sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          width: 28,
                                          height: 28,
                                          borderRadius: '6px',
                                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                          color: 'primary.main',
                                          mr: 1.5,
                                          flexShrink: 0,
                                          transition: 'all 0.2s ease'
                                        }}>
                                          {icon}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ 
                                          fontSize: '0.82rem',
                                          fontWeight: 500,
                                          lineHeight: 1.2
                                        }}>
                                          {label.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Box>
                          )}
                        </Box>
                        
                        {/* View Details Button */}
                        <Box sx={{ mt: 'auto', pt: 1 }}>
                          <Button 
                            variant="contained" 
                            color="primary" 
                            fullWidth
                            size="medium"
                            sx={{
                              borderRadius: 2,
                              py: 1,
                              textTransform: 'none',
                              fontWeight: 600,
                              letterSpacing: 0.5,
                              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                              '&:hover': {
                                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.3)',
                                transform: 'translateY(-1px)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            View Land
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {filteredProperties.length === 0 && !loading && (
                <Box sx={{ textAlign: 'center', py: 8, width: '100%' }}>
                  <Typography variant="h6" color="text.secondary">
                    No land found matching your criteria
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={clearFilters}
                    sx={{ mt: 2 }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              )}

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(e, page) => setCurrentPage(page)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default Properties;
